'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useCart } from './cart-provider';
import { Button } from '@sapling/ui/components/ui/button';
import { Skeleton } from '@sapling/ui/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@sapling/ui/components/ui/sheet';
import {
  XMarkIcon,
  MinusIcon,
  PlusIcon,
  ShoppingBagIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

function CartItemSkeleton() {
  return (
    <div className="flex gap-3 rounded-lg border p-3">
      <Skeleton className="h-16 w-16 shrink-0 rounded-md" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="h-4 w-16" />
    </div>
  );
}

export function CartSidebar() {
  const router = useRouter();
  const {
    cartId,
    items,
    totalItems,
    totalPrice,
    isLoading,
    updateQuantity,
    removeItem,
    isOpen,
    closeCart,
  } = useCart();

  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(priceInCents / 100);
  };

  const handleQuantityChange = (id: string, newQuantity: number) => {
    updateQuantity(id, newQuantity);
  };

  const handleCheckout = () => {
    if (!cartId) return;
    closeCart();
    router.push(`/butik/checkout/${cartId}/review`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBagIcon className="h-5 w-5" />
            Kundvagn ({totalItems})
          </SheetTitle>
          <SheetDescription>
            Granska dina produkter och gå till kassan
          </SheetDescription>
        </SheetHeader>

        <div className="flex h-[calc(100vh-8rem)] flex-col">
          {/* Cart Items */}
          <div className="flex-1 space-y-4 overflow-y-auto pr-2">
            {isLoading ? (
              Array.from({ length: Math.max(items.length, 1) }).map((_, i) => (
                <CartItemSkeleton key={i} />
              ))
            ) : items.length === 0 ? (
              <div className="py-12 text-center">
                <ShoppingBagIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-4 text-lg font-medium">
                  Din kundvagn är tom
                </h3>
                <p className="text-muted-foreground">
                  Lägg till produkter för att börja handla
                </p>
              </div>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex gap-3 rounded-lg border p-3">
                  {/* Product Image */}
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
                    {item.image ? (
                      <Image
                        src={item.image}
                        alt={item.name}
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                        Bild
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="min-w-0 flex-1">
                    <h4 className="line-clamp-2 text-sm font-medium">
                      {item.name}
                    </h4>
                    <p className="mt-1 text-sm font-bold text-primary">
                      {formatPrice(item.price)}
                    </p>

                    {/* Quantity Controls */}
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}
                        className="h-8 w-8 p-0"
                      >
                        <MinusIcon className="h-3 w-3" />
                      </Button>

                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity}
                      </span>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity + 1)
                        }
                        disabled={false}
                        className="h-8 w-8 p-0"
                      >
                        <PlusIcon className="h-3 w-3" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Item Total */}
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                    {item.quantity > 1 && (
                      <p className="text-xs text-muted-foreground">
                        {item.quantity} × {formatPrice(item.price)}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cart Footer - Always visible */}
          {(items.length > 0 || isLoading) && (
            <div className="mt-4 shrink-0 space-y-4 border-t bg-background pt-4">
              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium">Totalt:</span>
                {isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <span className="text-2xl font-bold">
                    {formatPrice(totalPrice)}
                  </span>
                )}
              </div>

              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                className="w-full"
                size="lg"
                disabled={isLoading || items.length === 0}
              >
                <CreditCardIcon className="mr-2 h-5 w-5" />
                Gå till kassan
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Säker betalning med Swish
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
